import React, { useEffect, useRef, useState } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import {
  Button,
  Drawer,
  TextInputWithLabel,
  PopupMenu,
  Checkbox
} from 'quantux-ui'
import PatientContactCard from './patient-contact-card';
import { gray, salmon, blue } from '../../theme';
import { BASE_URL, titleCase } from '../../utils';

const PatientContact = (props) => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [contactNumber, setContactNumber] = useState();
  const [contactType, setContactType] = useState('mobile');
  const [relationToPatient, setRelationToPatient] = useState('');
  const [hasPowerOfAttorney, setHasPowerOfAttorney] = useState(false);
  const [showRelationMenu, setShowRelationMenu] = useState(false);
  const [isContactMenuOpen, setIsContactMenuOpen] = useState(false);
  const [isInEditMode, setIsInEditMode] = useState(false);
  const [activeContactId, setActiveContactId] = useState('');

  // Refs
  const relationRef = useRef();
  const contactRef = useRef();

  // Styles
  const styles = StyleSheet.create({
    sideDrawerContainer: {
      // background: gray[600],
      background: '#002448',
      color: gray[300],
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexFlow: 'column'
    },
    sideDrawerHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px',
      fontWeight: 'bold',
      borderBottom: '1px solid black',
      flexShrink: '0'
    },
    sideDrawerHeaderCloseIcon: {
      color: gray[300],
      ':hover': {
        color: salmon[500]
      }
    },
    sideDrawerBanner: {
      position: 'relative',
      padding: '8px',
      flexShrink: '0'
    },
    sideDrawerBannerImg: {
      width: '100%',
      height: 'auto'
    },
    sideDrawerBannerCameraIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      right: '0',
      bottom: '8px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    sideDrawerContentArea: {
      // height: 'calc(100vh - 186px)',
      // height: `${window.innerHeight - 170}px`,
      overflowY: 'auto',
      color: gray[300],
      flexGrow: '1',
      padding: '8px'
    },
    sideDrawerUserCardAvatar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '80px',
      height: '80px',
      color: 'white',
      borderRadius: '4px',
      position: 'relative'
    },
    sideDrawerUserCardAvatarImg: {
      width: 'auto',
      height: '80px',
      maxHeight: '80px'
    },
    sideDrawerAvatarCamera: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      left: '-3px',
      top: '58px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    sideDrawerUserCardText: {
      fontSize: '12px'
    },
    sideDrawerFooter: {
      alignItems: 'center',
      justifyContent: 'flex-end',
      background: gray[700],
      height: '56px',
      width: '100%',
      borderTop: `1px solid ${gray[500]}`,
      flexShrink: '0',
      paddingRight: '8px'
    },
    sideDrawerLinkStyles: {
      color: gray[300],
      ':visited': {
        color: gray[300]
      }
    },
    sideDrawerSelectedLink: {
      color: blue[500]
    },
    popupMenuContainer: {
      background: '#083050',
      width: '120px',
      overflowY: 'auto',
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.5)'
    },
    popupMenuItem: {
      height: '60px',
      background: blue[700],
      padding: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      cursor: 'pointer',
      ':hover': {
        color: blue[200],
        background: blue[900]
      }
    },
    menuItemIconContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '60px',
      minHeight: '60px',
      width: '40px',
      minWidth: '40px'
    },
    selectedPopupMenuItemSmall: {
      backgroundColor: '#0066CC',
      color: 'white'
    },
    menuItemIcon: {
      minWidth: '32px',
      fontSize: '20px'
    },
    popupMenuItemSmall: {
      height: '25px',
      display: 'flex',
      fontSize: '14px',
      alignItems: 'center',
      cursor: 'pointer',
      color: '#5080B0',
      ':not(.selected):hover': {
        backgroundColor: '#0066CC',
        color: 'white'
      }
    },
    menuItemIconContainerSmall: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '25px',
      width: '25px'
    },
    // menuItemIconContainer: {
    //   display: 'flex',
    //   alignItems: 'center',
    //   justifyContent: 'center',
    //   height: '40px',
    //   width: '40px'
    // },
    menuItemIconSmall: {
      fontSize: '14px'
    }
  });

  // Event Handlers
  const toggleDrawer = () => {
    setFirstName('');
    setLastName('');
    setContactNumber('');
    setContactType('mobile');
    setRelationToPatient('');
    setHasPowerOfAttorney(false);
    setShowDrawer(true);
    setIsInEditMode(false);
  };

  const handleOnClickAddContactPerson = () => {
    const formData = new FormData();
    formData.append('patientId', props.patientData._id);
    formData.append('contactPersonFirstName', firstName);
    formData.append('contactPersonLastName', lastName);
    formData.append('contactPersonContactNumber', contactNumber);
    formData.append('contactType', contactType);
    formData.append('contactPersonRelationToPatient', relationToPatient);
    formData.append('hasPowerOfAttorney', hasPowerOfAttorney);

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
      // headers: reqHeaders
    };

    fetch(`${BASE_URL}/patients/addContactPerson`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          setShowDrawer(false);
          props.updateFlag();
        }
      })
      .catch(error => console.log('error', error));
  };

  const updateContact = () => {
    const formData = new FormData();
    formData.append('patientId', props.patientData._id);
    formData.append('contactId', activeContactId);
    formData.append('contactPersonFirstName', firstName);
    formData.append('contactPersonLastName', lastName);
    formData.append('contactPersonContactNumber', contactNumber);
    formData.append('contactType', contactType);
    formData.append('contactPersonRelationToPatient', relationToPatient);
    formData.append('hasPowerOfAttorney', hasPowerOfAttorney);

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
      // headers: reqHeaders
    };

    fetch(`${BASE_URL}/patients/updateContactPerson`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          setShowDrawer(false);
          props.updateFlag();
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnEditFirstName = (e) => {
    const { value } = e.target;
    setFirstName(value);
  };

  const handleOnEditLastName = (e) => {
    const { value } = e.target;
    setLastName(value);
  };

  const handleOnEditContactNumber = (e) => {
    const { value } = e.target;
    setContactNumber(value);
  };

  const handleOnEditContactCard = (data) => {
    const {
      contactId,
      contactPersonLastName,
      contactPersonFirstName,
      contactPersonContactNumber,
      contactType = 'mobile',
      contactPersonRelationToPatient,
      hasPowerOfAttorney = false
    } = data;

    setActiveContactId(contactId);
    setFirstName(contactPersonFirstName);
    setLastName(contactPersonLastName);
    setContactNumber(contactPersonContactNumber);
    setContactType(contactType);
    setRelationToPatient(contactPersonRelationToPatient);
    setHasPowerOfAttorney(hasPowerOfAttorney);
    setIsInEditMode(true);
    setShowDrawer(true);
  };

  const handleOnDeleteContactCard = (data) => {
    const formData = new FormData();
    formData.append('patientId', props.patientData._id);
    formData.append('contactId', data.contactId);

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
      // headers: reqHeaders
    };

    fetch(`${BASE_URL}/patients/removeContactPerson`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          props.updateFlag();
        }
      })
      .catch(error => console.log('error', error));
  };

  let cpsWithLoA = [];
  const cps = [];

  if (props.patientData.contactPersons && props.patientData.contactPersons.length > 0) {
    props.patientData.contactPersons.forEach(el => {
      if (el.hasPowerOfAttorney) {
        cpsWithLoA.push(el);
      } else {
        cps.push(el);
      }
    })
  }
  cpsWithLoA = cpsWithLoA.concat(cps);

  return (
    <div
      className='p-4'
      style={{
        height: 'calc(100% - 140px)',
        overflowY: 'auto'
      }}
    >
      {showDrawer &&
        <Drawer
          placement="right"
          visible={showDrawer}
          toggleDrawer={() => setShowDrawer(false)}
          zIndex="2000"
          width={window.innerWidth <= 700 ? window.innerWidth : 375}
        >
          {/* Drawer Container */}
          <div className={css(styles.sideDrawerContainer)}>
            {/* Header */}
            <div className={css(styles.sideDrawerHeader)}>
              <div className='fx fx__hv-center'>
                <i className='mr-2 fa fa-lg fa-user' />
                <div className='ml-2'>Add Contact Person</div>
              </div>
              <div className='fx fx__v-center'>
                <Button
                  btnType='primary'
                  onClick={() => {
                    if (isInEditMode) {
                      updateContact();
                    } else {
                      handleOnClickAddContactPerson();
                    }
                  }}
                  style={{
                    width: '90px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px'
                  }}
                >
                  {isInEditMode ? 'Update' : 'Save Contact'}
                </Button>
                <i
                  className={`ml-2 fa fa-lg fa-times-circle ${css(styles.sideDrawerHeaderCloseIcon)}`}
                  onClick={() => setShowDrawer(false)}
                />
              </div>
            </div>
            {/* Content */}
            <div className={css(styles.sideDrawerContentArea)}>
              {/* Patient First Name */}
              <div>
                <TextInputWithLabel
                  value={firstName}
                  titleText="First Name"
                  titleFontSize={12}
                  isCompactLayout={true}
                  titleColor={gray[200]}
                  textColor="white"
                  selectionColor="white"
                  selectionBackground="#0080FF"
                  spellcheck={false}
                  width={343}
                  onChange={handleOnEditFirstName}
                />
              </div>
              {/* Patient Last Name */}
              <div className='mt-2'>
                <TextInputWithLabel
                  value={lastName}
                  titleText="Last Name"
                  titleFontSize={12}
                  isCompactLayout={true}
                  titleColor={gray[200]}
                  textColor="white"
                  selectionColor="white"
                  selectionBackground="#0080FF"
                  spellcheck={false}
                  width={343}
                  onChange={handleOnEditLastName}
                />
              </div>
              {/* Has power of attorney */}
              <div className='mt-4'>
                <Checkbox
                  text='Has Power Of Attorney'
                  isChecked={hasPowerOfAttorney}
                  onChange={() => {
                    setHasPowerOfAttorney(!hasPowerOfAttorney);
                  }}
                />
              </div>
              {/* Relation To Patient */}
              <div className='fx mt-4'>
                <div
                  className='mr-2 fx'
                  ref={relationRef}
                  style={{ position: 'relative' }}
                >
                  Relation To Patient:&nbsp;
                  <div
                    className='ml-2'
                    style={{
                      color: relationToPatient === '' ? salmon[500] : blue[500],
                      cursor: 'pointer'
                    }}
                    onClick={() => setShowRelationMenu(true)}
                  >
                    {relationToPatient === '' ? 'Select' : relationToPatient}&nbsp;
                    <i className='fa fa-caret-down' />
                  </div>
                  <PopupMenu
                    top={28}
                    right={1}
                    visible={showRelationMenu}
                    parent={relationRef}
                    position="absolute"
                    onOutsideClick={() => { setShowRelationMenu(false); }}
                  >
                    <div className={css(styles.popupMenuContainer)}>
                      {/* Spouse */}
                      <div
                        className={css(styles.popupMenuItem)}
                        style={{
                          height: '32px',
                          padding: '8px',
                          borderLeft: relationToPatient === 'Spouse' ? `2px solid ${blue[500]}` : '',
                          background: relationToPatient === 'Spouse' ? blue[800] : ''
                        }}
                        onClick={() => {
                          setShowRelationMenu(false);
                          setRelationToPatient('Spouse');
                        }}
                      >
                        <div style={{ fontSize: '12px' }}>Spouse</div>
                      </div>
                      {/* Parent */}
                      <div
                        className={css(styles.popupMenuItem)}
                        style={{
                          height: '32px',
                          padding: '8px',
                          borderLeft: relationToPatient === 'Parent' ? `2px solid ${blue[500]}` : '',
                          background: relationToPatient === 'Parent' ? blue[800] : ''
                        }}
                        onClick={() => {
                          setShowRelationMenu(false);
                          setRelationToPatient('Parent');
                        }}
                      >
                        <div style={{ fontSize: '12px' }}>Parent</div>
                      </div>
                      {/* Legal Guardian */}
                      <div
                        className={css(styles.popupMenuItem)}
                        style={{
                          height: '32px',
                          padding: '8px',
                          borderLeft: relationToPatient === 'Legal Guardian' ? `2px solid ${blue[500]}` : '',
                          background: relationToPatient === 'Legal Guardian' ? blue[800] : ''
                        }}
                        onClick={() => {
                          setShowRelationMenu(false);
                          setRelationToPatient('Legal Guardian');
                        }}
                      >
                        <div style={{ fontSize: '12px' }}>Legal Guardian</div>
                      </div>
                      {/* Child */}
                      <div
                        className={css(styles.popupMenuItem)}
                        style={{
                          height: '32px',
                          padding: '8px',
                          borderLeft: relationToPatient === 'Child' ? `2px solid ${blue[500]}` : '',
                          background: relationToPatient === 'Child' ? blue[800] : ''
                        }}
                        onClick={() => {
                          setShowRelationMenu(false);
                          setRelationToPatient('Child');
                        }}
                      >
                        <div style={{ fontSize: '12px' }}>Child</div>
                      </div>
                      {/* Sibling */}
                      <div
                        className={css(styles.popupMenuItem)}
                        style={{
                          height: '32px',
                          padding: '8px',
                          borderLeft: relationToPatient === 'Sibling' ? `2px solid ${blue[500]}` : '',
                          background: relationToPatient === 'Sibling' ? blue[800] : ''
                        }}
                        onClick={() => {
                          setShowRelationMenu(false);
                          setRelationToPatient('Sibling');
                        }}
                      >
                        <div style={{ fontSize: '12px' }}>Sibling</div>
                      </div>
                      {/* Other */}
                      <div
                        className={css(styles.popupMenuItem)}
                        style={{
                          height: '32px',
                          padding: '8px',
                          borderLeft: relationToPatient === 'Other' ? `2px solid ${blue[500]}` : '',
                          background: relationToPatient === 'Other' ? blue[800] : ''
                        }}
                        onClick={() => {
                          setShowRelationMenu(false);
                          setRelationToPatient('Other');
                        }}
                      >
                        <div style={{ fontSize: '12px' }}>Other</div>
                      </div>
                    </div>
                  </PopupMenu>
                </div>
              </div>
              {/* Contact # (Mobile or Phone) */}
              <div
                className='fx mt-4'
                style={{
                  position: 'relative',
                  padding: '0'
                }}
              >
                <div>
                  <TextInputWithLabel
                    value={contactNumber}
                    titleText="Contact #"
                    isCompactLayout={false}
                    showPreIcon
                    preIcon={`fa fa-lg ${contactType.toLowerCase() === 'mobile' ? 'fa-mobile' : 'fa-phone'}`}
                    titleColor="#60A0E0"
                    textColor="#0080FF"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={174}
                    placeholder='Required'
                    onChange={handleOnEditContactNumber}
                  />
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    position: 'relative',
                    left: '-90px',
                    color: blue[500]
                  }}
                  ref={contactRef}
                  onClick={() => setIsContactMenuOpen(true)}
                >
                  {contactType.toLowerCase() === 'mobile' ? 'Mobile' : 'Landline'}&nbsp;&nbsp;<i style={{ fontSize: '14px' }} className='fa fa-caret-down' />
                </div>
                <PopupMenu
                  top={20}
                  left={84}
                  visible={isContactMenuOpen}
                  parent={contactRef}
                  position="absolute"
                  onOutsideClick={() => { setIsContactMenuOpen(false); }}
                >
                  <div
                    className={css(styles.popupMenuContainer)}
                    style={{ width: '110px' }}
                  >
                    {/* Mobile */}
                    <div
                      className={`${contactType === 'mobile' ? 'selected' : ''} ${css(styles.popupMenuItemSmall, contactType === 'mobile' && styles.selectedPopupMenuItemSmall)}`}
                      onClick={() => {
                        setIsContactMenuOpen(false);
                        setContactType('mobile');
                      }}
                    >
                      <div className={css(styles.menuItemIconContainerSmall)}>
                        <i className={`fa fa-mobile ${css(styles.menuItemIconSmall)}`} />
                      </div>
                      <div>Mobile</div>
                    </div>
                    {/* Landline */}
                    <div
                      className={`${contactType === 'landline' ? 'selected' : ''} ${css(styles.popupMenuItemSmall, contactType === 'landline' && styles.selectedPopupMenuItemSmall)}`}
                      onClick={() => {
                        setIsContactMenuOpen(false);
                        setContactType('landline');
                      }}
                    >
                      <div className={css(styles.menuItemIconContainerSmall)}>
                        <i className={`fa fa-phone ${css(styles.menuItemIconSmall)}`} />
                      </div>
                      <div>Landline</div>
                    </div>
                  </div>
                </PopupMenu>
              </div>
            </div>
          </div>
        </Drawer>
      }
      <Button
        btnType='primary'
        onClick={toggleDrawer}
        style={{
          width: '359px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div>Add Contact Person</div>
        </div>
      </Button>
      {/* Contact person cards */}
      {cpsWithLoA.length > 0 &&
        cpsWithLoA.map((el) => (
          <PatientContactCard
            key={Math.random().toString(36).slice(2)}
            data={el}
            onEdit={handleOnEditContactCard}
            onDelete={handleOnDeleteContactCard}
          />
        ))
      }
    </div>
  );
};

export default PatientContact;
